import {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useClickOutside } from '../Hooks/useClickOutside';
import { SelectOption } from '../types';
import './ListSelect.css';

const SEARCH_DEBOUNCE_MS = 400;
const VIEWPORT_GUTTER = 8;

interface ListSelectProps {
  options: SelectOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;

  multiSelect?: boolean;
  searchable?: boolean;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  totalCount?: number;

  label?: string;
  placeholder?: string;
  fallbackLabel?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  loadingText?: string;

  disabled?: boolean;
  id?: string;
  className?: string;

  width?: string | number;
  panelWidth?: string | number;
  maxHeight?: string | number;

  maxVisibleOptions?: number;
  footer?: ReactNode;
  showCount?: boolean;
}

interface ListSelectRowProps {
  value: string;
  label: string;
  selected: boolean;
  onToggle: (value: string) => void;
}

function ListSelect({
  options,
  value,
  onChange,
  multiSelect = false,
  searchable = false,
  onSearch,
  onLoadMore,
  hasMore = false,
  totalCount,
  label,
  placeholder = 'Select an option',
  fallbackLabel,
  searchPlaceholder,
  emptyText = 'No matches',
  loading = false,
  loadingText = 'Loading…',
  disabled = false,
  id,
  className = '',
  width,
  panelWidth,
  maxHeight,
  maxVisibleOptions,
  footer,
  showCount = true,
}: ListSelectProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [placement, setPlacement] = useState<{ up: boolean; right: boolean }>({
    up: false,
    right: false,
  });

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);

  const isPaginated = Boolean(onLoadMore);

  const selectedSet = useMemo(() => {
    if (multiSelect) return new Set(Array.isArray(value) ? value : []);
    return new Set(value ? [value as string] : []);
  }, [value, multiSelect]);

  const selectedLabels = useMemo(() => {
    if (selectedSet.size === 0) return [];
    const labels: string[] = [];
    for (const option of options) {
      if (selectedSet.has(option.value)) labels.push(String(option.label ?? '') || option.value);
    }
    return labels;
  }, [options, selectedSet]);

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query || onSearch) return options;
    return options.filter(
      (option) =>
        String(option.label ?? '')
          .toLowerCase()
          .includes(query) || String(option.value).toLowerCase() === query,
    );
  }, [options, debouncedSearch, onSearch]);

  const isTruncated =
    !isPaginated && maxVisibleOptions !== undefined && filtered.length > maxVisibleOptions;

  // The selected option is pinned to the top when it falls outside the cap, so an
  // existing value is never invisible. Skipped while searching to keep hits pure.
  const visible = useMemo(() => {
    if (!isTruncated) return filtered;
    const capped = filtered.slice(0, maxVisibleOptions);
    if (debouncedSearch.trim() || selectedSet.size === 0) return capped;

    const cappedValues = new Set(capped.map((option) => option.value));
    const pinned = options.filter(
      (option) => selectedSet.has(option.value) && !cappedValues.has(option.value),
    );
    return pinned.length ? [...pinned, ...capped] : capped;
  }, [filtered, isTruncated, maxVisibleOptions, debouncedSearch, selectedSet, options]);

  const latest = useRef({ value, multiSelect, onChange, onSearch, onLoadMore });
  latest.current = { value, multiSelect, onChange, onSearch, onLoadMore };

  const handleToggle = useCallback((optionValue: string) => {
    const { value: current, multiSelect: multi, onChange: emit } = latest.current;

    if (multi) {
      const list = Array.isArray(current) ? current : [];
      emit(
        list.includes(optionValue)
          ? list.filter((item) => item !== optionValue)
          : [...list, optionValue],
      );
      return;
    }

    if (current !== optionValue) emit(optionValue);
    setIsOpen(false);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);
  useClickOutside(rootRef, close, isOpen);

  const handleTriggerClick = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
    },
    [disabled],
  );

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (isOpen) latest.current.onSearch?.(debouncedSearch.trim());
  }, [isOpen, debouncedSearch]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!isOpen || !hasMore || !sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) latest.current.onLoadMore?.();
      },
      { root: listRef.current, rootMargin: '0px 0px 80px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, hasMore, options]);

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
      return undefined;
    }
    setSearch('');
    setDebouncedSearch('');
    setPlacement({ up: false, right: false });
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const update = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;

      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const up = spaceBelow < panel.offsetHeight + VIEWPORT_GUTTER && spaceAbove > spaceBelow;
      const right =
        panel.offsetWidth > rect.width &&
        rect.left + panel.offsetWidth > window.innerWidth - VIEWPORT_GUTTER;

      setPlacement((prev) => (prev.up === up && prev.right === right ? prev : { up, right }));
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isOpen, visible.length]);

  const countFooter = (() => {
    if (!showCount) return null;
    if (multiSelect && selectedSet.size > 0) return `${selectedSet.size} selected`;
    if (isTruncated) {
      const total = `Showing ${maxVisibleOptions} of ${filtered.length}`;
      return searchable ? `${total} · type to narrow down` : total;
    }
    return null;
  })();

  const footerContent = footer ?? countFooter;

  const itemCount = totalCount ?? (isPaginated ? undefined : options.length);
  const searchInputPlaceholder =
    searchPlaceholder ??
    (itemCount ? `Search ${itemCount.toLocaleString('en-US')} items…` : 'Search…');

  const triggerLabel = (() => {
    if (selectedLabels.length) return selectedLabels.join(', ');
    if (selectedSet.size === 0) return '';
    if (fallbackLabel) return fallbackLabel;
    return loading ? loadingText : '';
  })();

  const panelClassName = [
    'list-select-panel',
    panelWidth ? 'list-select-panel--sized' : '',
    placement.up ? 'list-select-panel--up' : '',
    placement.right ? 'list-select-panel--right' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      id={id}
      ref={rootRef}
      className={`list-select ${disabled ? 'is-disabled ' : ''}${className}`.trim()}
      style={width ? { width } : undefined}
    >
      {label && <span className="list-select-label">{label}</span>}

      <div
        ref={triggerRef}
        className={`list-select-trigger d-flex align-center${isOpen ? ' is-open' : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
      >
        <span
          className={`list-select-value${triggerLabel ? '' : ' is-placeholder'}`}
          title={triggerLabel}
        >
          {triggerLabel || placeholder}
        </span>
        <span className={`list-select-chevron${isOpen ? ' is-open' : ''}`}>
          <i className="fa-solid fa-chevron-down" />
        </span>
      </div>

      {isOpen && (
        <div
          ref={panelRef}
          className={panelClassName}
          style={panelWidth ? { width: panelWidth } : undefined}
        >
          {searchable && (
            <div className="list-select-search">
              <label className="list-select-search-field d-flex align-center">
                <i className="fa-solid fa-magnifying-glass list-select-search-icon" />
                <input
                  ref={searchRef}
                  className="list-select-search-input"
                  type="text"
                  value={search}
                  placeholder={searchInputPlaceholder}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
            </div>
          )}

          <ul
            ref={listRef}
            className="list-select-options"
            style={maxHeight ? { maxHeight } : undefined}
          >
            {visible.map((option) => (
              <ListSelectRow
                key={option.value}
                value={option.value}
                label={String(option.label ?? '')}
                selected={selectedSet.has(option.value)}
                onToggle={handleToggle}
              />
            ))}
            {visible.length === 0 && (
              <li className="list-select-empty">{loading ? loadingText : emptyText}</li>
            )}
            {isPaginated && visible.length > 0 && loading && (
              <li className="list-select-empty">{loadingText}</li>
            )}
            {isPaginated && hasMore && (
              <li ref={sentinelRef} className="list-select-sentinel" aria-hidden />
            )}
          </ul>

          {footerContent && <div className="list-select-footer small-font">{footerContent}</div>}
        </div>
      )}
    </div>
  );
}

const ListSelectRow = memo(function ListSelectRow({
  value,
  label,
  selected,
  onToggle,
}: ListSelectRowProps) {
  const handleClick = useCallback(() => onToggle(value), [onToggle, value]);

  return (
    <li
      className={`list-select-option d-flex align-center${selected ? ' is-selected' : ''}`}
      onClick={handleClick}
      title={label || value}
    >
      <input type="checkbox" className="list-select-option-box" checked={selected} readOnly />
      <span className="list-select-option-label">{label || value}</span>
    </li>
  );
});

export default ListSelect;
